import { cartPricesAsOf, getRootUrl } from '@share-a-cart/shared-utils';
import type { jsPDF } from 'jspdf';
import type { Cell } from 'jspdf-autotable';
import type { ExportableCart } from '../../types/cart';
import { localUrl } from '../i18n/langBasename';
import { buildCartPdfRows } from './buildCartPdfRows';
import {
  embeddedPdfFonts,
  PDF_FONT_FILES,
  pdfFontFor,
  pdfTextRuns,
  type EmbeddedPdfFont,
  type PdfFont,
} from './pdfFonts';

export type CartPdfLabels = {
  locale: string;
  cartName: string;
  cartId: string;
  created: string;
  pricesAsOf: string;
  vendor: string;
  item: string;
  id: string;
  sku: string;
  qty: string;
  unit: string;
  total: string;
  page: (page: number, total: number) => string;
};

type Rgb = [number, number, number];
type LabelledText = readonly [label: string, value: string];
type TextOptions = { align?: 'left' | 'right'; bold?: boolean; url?: string; base?: PdfFont };

const MARGIN = 40;
const BRAND_BLUE: Rgb = [48, 50, 220];
const TEXT_DARK: Rgb = [33, 37, 41];
const TEXT_MUTED: Rgb = [107, 114, 128];
const NUMERIC_COLUMNS_FROM = 3;
const HEADER_LINE_HEIGHT = 14;

const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
};

type LoadedPdfFont = readonly [EmbeddedPdfFont, string];

const loadFonts = (fonts: EmbeddedPdfFont[]): Promise<LoadedPdfFont[]> =>
  Promise.all(
    fonts.map(async (font) => {
      const response = await fetch(`/fonts/pdf/${PDF_FONT_FILES[font]}`);
      if (!response.ok) throw new Error(`PDF font ${font} failed to load (${response.status})`);
      return [font, toBase64(await response.arrayBuffer())] as const;
    }),
  );

const embedFonts = (doc: jsPDF, loaded: LoadedPdfFont[]) => {
  for (const [font, base64] of loaded) {
    const file = PDF_FONT_FILES[font];
    doc.addFileToVFS(file, base64);
    doc.addFont(file, font, 'normal');
  }
};

export const exportCartToPdf = async (cart: ExportableCart, labels: CartPdfLabels) => {
  const table = buildCartPdfRows(cart);
  const head = [labels.item, labels.id, labels.sku, labels.qty, labels.unit, labels.total];
  const localTime = (time: number) => new Date(time).toLocaleString(labels.locale);
  const pricesAsOf = cartPricesAsOf(cart);
  const createdLine: LabelledText = [labels.created, localTime(cart.timestamp)];
  const pricesAsOfLine: LabelledText | null =
    pricesAsOf === undefined ? null : [labels.pricesAsOf, localTime(pricesAsOf)];
  const nameLine: LabelledText | null = cart.title ? [labels.cartName, cart.title] : null;
  const idLine: LabelledText = [labels.cartId, cart.cartId];
  const vendorLine: LabelledText | null = cart.store ? [labels.vendor, cart.store] : null;
  const body = table.rows.map((row) => [
    row.title,
    row.id,
    row.sku,
    String(row.quantity),
    row.unit,
    row.total,
  ]);
  const linkByRow = new Map<unknown, string>(body.map((cells, i) => [cells, table.rows[i].link]));

  const [{ jsPDF }, { autoTable }, fonts] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    loadFonts(
      embeddedPdfFonts([
        ...head,
        ...[createdLine, pricesAsOfLine, nameLine, idLine, vendorLine].flatMap((line) => line ?? []),
        labels.page(1, 1),
        table.grandTotal,
        ...table.rows.flatMap((row) => [row.title, row.id, row.sku, row.unit, row.total]),
      ]),
    ),
  ]);

  const doc = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
  embedFonts(doc, fonts);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const setTextStyle = (size: number, color: Rgb) => {
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const runsOf = (text: string, { bold = false, base }: TextOptions) =>
    pdfTextRuns(text, base).map((run) => {
      doc.setFont(run.font, bold && run.font === 'helvetica' ? 'bold' : 'normal');
      return { ...run, width: doc.getTextWidth(run.text) };
    });

  const textWidth = (text: string, options: TextOptions = {}) =>
    runsOf(text, options).reduce((width, run) => width + run.width, 0);

  const drawText = (text: string, x: number, y: number, options: TextOptions = {}) => {
    const runs = runsOf(text, options);
    let runX = options.align === 'right' ? x - runs.reduce((width, run) => width + run.width, 0) : x;
    for (const run of runs) {
      doc.setFont(run.font, options.bold && run.font === 'helvetica' ? 'bold' : 'normal');
      if (options.url) doc.textWithLink(run.text, runX, y, { url: options.url });
      else doc.text(run.text, runX, y);
      runX += run.width;
    }
  };

  const wrapText = (text: string, maxWidth: number, options: TextOptions = {}): string[] => {
    const measure = { ...options, base: options.base ?? pdfFontFor(text) };
    const fits = (line: string) => textWidth(line.trimEnd(), measure) <= maxWidth;
    const lines: string[] = [];
    for (const paragraph of text.split(/\r\n|\r|\n/)) {
      let line = '';
      for (const word of paragraph.match(/\S*\s*/g) ?? []) {
        if (fits(line + word)) {
          line += word;
          continue;
        }
        if (line) lines.push(line.trimEnd());
        line = '';
        for (const char of Array.from(word)) {
          if (line && !fits(line + char)) {
            lines.push(line.trimEnd());
            line = '';
          }
          line += char;
        }
      }
      lines.push(line.trimEnd());
    }
    return lines;
  };

  const drawLabelled = ([label, value]: LabelledText, y: number, color: Rgb, url?: string): number => {
    setTextStyle(10, color);
    const valueX = MARGIN + textWidth(`${label} `);
    drawText(label, MARGIN, y, { url });
    const base = pdfFontFor(value);
    const lines = wrapText(value, pageWidth - MARGIN - valueX, { base });
    lines.forEach((line, i) => drawText(line, valueX, y + i * HEADER_LINE_HEIGHT, { url, base }));
    return y + lines.length * HEADER_LINE_HEIGHT;
  };

  const drawLabelledRight = ([label, value]: LabelledText, y: number) => {
    const right = pageWidth - MARGIN;
    setTextStyle(10, TEXT_MUTED);
    drawText(value, right, y, { align: 'right' });
    drawText(`${label} `, right - textWidth(value), y, { align: 'right' });
  };

  // autotable draws a cell in one face, so cells needing several are wrapped here and drawn in didDrawCell.
  let mixedCellLines: string[] | null = null;

  const drawCellLines = (cell: Cell, lines: string[]) => {
    const { x, y } = cell.getTextPos();
    const fontSize = cell.styles.fontSize / doc.internal.scaleFactor;
    const lineHeight = fontSize * doc.getLineHeightFactor();
    // The same first baseline autotable gives its own cell text.
    const firstBaseline = y + fontSize * (2 - 1.15);
    const align = cell.styles.halign === 'right' ? 'right' : 'left';
    const base = cell.styles.font as PdfFont;
    lines.forEach((line, i) => drawText(line, x, firstBaseline + i * lineHeight, { align, base }));
  };

  let y = 50;
  setTextStyle(16, TEXT_DARK);
  drawText('Share-A-Cart.com', MARGIN, y, { bold: true, url: getRootUrl() });
  if (pricesAsOfLine) {
    drawLabelledRight(createdLine, y - HEADER_LINE_HEIGHT / 2);
    drawLabelledRight(pricesAsOfLine, y + HEADER_LINE_HEIGHT / 2);
  } else {
    drawLabelledRight(createdLine, y);
  }
  y += 22;

  if (nameLine) y = drawLabelled(nameLine, y, TEXT_DARK);
  y = drawLabelled(idLine, y, BRAND_BLUE, `${getRootUrl()}${localUrl(`/get/${cart.cartId}`)}`);
  if (vendorLine) y = drawLabelled(vendorLine, y, TEXT_DARK);

  autoTable(doc, {
    startY: y + 8,
    margin: { left: MARGIN, right: MARGIN, bottom: MARGIN + 10 },
    head: [head],
    body,
    foot: [
      [
        { content: labels.total, colSpan: NUMERIC_COLUMNS_FROM },
        String(table.totalQuantity),
        '',
        table.grandTotal,
      ],
    ],
    showFoot: 'lastPage',
    rowPageBreak: 'avoid',
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak', textColor: TEXT_DARK },
    headStyles: { fillColor: BRAND_BLUE, textColor: 255 },
    footStyles: { fillColor: 255, textColor: TEXT_DARK, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 184 },
      1: { cellWidth: 76 },
      2: { cellWidth: 84 },
      3: { cellWidth: 40 },
      4: { cellWidth: 74 },
      5: { cellWidth: 74 },
    },
    didParseCell: (data) => {
      const text = data.cell.text.join('\n');
      const font = pdfFontFor(text);
      data.cell.styles.font = font;
      if (font !== 'helvetica') data.cell.styles.fontStyle = 'normal';
      if (pdfTextRuns(text, font).length > 1) {
        data.cell.styles.overflow = (lines, width) =>
          wrapText([lines].flat().join('\n'), width + 1 / doc.internal.scaleFactor, { base: font });
      }
      if (data.column.index >= NUMERIC_COLUMNS_FROM || data.section === 'foot') {
        data.cell.styles.halign = 'right';
      }
      if (data.section === 'body' && data.column.index === 0 && linkByRow.get(data.row.raw)) {
        data.cell.styles.textColor = BRAND_BLUE;
      }
    },
    willDrawCell: (data) => {
      if (typeof data.cell.styles.overflow !== 'function') return;
      mixedCellLines = data.cell.text;
      data.cell.text = [];
    },
    didDrawCell: (data) => {
      if (mixedCellLines) {
        drawCellLines(data.cell, mixedCellLines);
        data.cell.text = mixedCellLines;
        mixedCellLines = null;
      }
      if (data.section !== 'body' || data.column.index !== 0) return;
      const link = linkByRow.get(data.row.raw);
      if (link) doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: link });
    },
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    const label = labels.page(page, pageCount);
    setTextStyle(8, TEXT_MUTED);
    drawText(label, pageWidth - MARGIN, pageHeight - 24, { align: 'right' });
  }

  doc.save(`${cart.title ? `${cart.title} - ${cart.cartId}` : cart.cartId}.pdf`);
};
